import React, { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntakeLandingPage = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    website: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    if (!formData.companyName || !formData.firstName || !formData.lastName || 
        !formData.phone || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/intake/submit`, formData);
      
      if (response.data.status === 'success') {
        setSubmitted(true);
        toast.success('Application request received!');
        toast.success(`Check your email (${formData.email}) for confirmation`, { duration: 8000 });
      }
    } catch (error) {
      console.error('Error submitting intake form:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to submit. Please try again.';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="max-w-2xl w-full bg-slate-800 p-12 rounded-2xl shadow-2xl text-center">
          <div className="mb-6">
            <svg className="w-20 h-20 text-green-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-slate-100 mb-4">We Received Your Request!</h2>
          <p className="text-slate-300 text-lg mb-3">
            A verification email has been sent to:
          </p>
          <p className="text-slate-100 font-semibold text-xl mb-6">{formData.email}</p>
          <p className="text-slate-400 mb-8">
            Please check your inbox and click the confirmation link to verify your email. Once confirmed, we'll begin processing your application and send you a secure onboarding link.
          </p>
          <Button 
            onClick={() => setSubmitted(false)}
            variant="outline"
            className="border-slate-600 text-slate-200 hover:bg-slate-700"
          >
            Submit Another Application
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-12">
      <div className="max-w-3xl w-full">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-slate-100 mb-4">Start Your Merchant Application</h1>
          <p className="text-slate-300 text-xl">PaySec Merchant Onboarding</p>
          <p className="text-slate-400 mt-4">Fill in your basic information to start your application</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-800 p-8 rounded-2xl shadow-2xl">
          <div className="space-y-6">
            <div>
              <Label htmlFor="companyName" className="text-slate-200 text-base">Corporate / Legal Name *</Label>
              <Input
                id="companyName"
                value={formData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                className="mt-2 bg-slate-900 border-slate-700 text-slate-100 h-12 text-base"
                placeholder="Enter your company name"
                required
                data-testid="intake-company-name"
              />
            </div>

            <div>
              <Label htmlFor="website" className="text-slate-200 text-base">Website Address</Label>
              <div className="mt-2 flex">
                <span className="inline-flex items-center px-3 bg-slate-800 border border-r-0 border-slate-700 rounded-l-md text-slate-400 text-base select-none">
                  https://
                </span>
                <Input
                  id="website"
                  value={formData.website.replace(/^https?:\/\//, '')}
                  onChange={(e) => handleChange('website', 'https://' + e.target.value.replace(/^https?:\/\//, ''))}
                  className="bg-slate-900 border-slate-700 text-slate-100 h-12 text-base rounded-l-none"
                  placeholder="yourcompany.com"
                  data-testid="intake-website"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="firstName" className="text-slate-200 text-base">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className="mt-2 bg-slate-900 border-slate-700 text-slate-100 h-12 text-base"
                  placeholder="John"
                  required
                  data-testid="intake-first-name"
                />
              </div>

              <div>
                <Label htmlFor="lastName" className="text-slate-200 text-base">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className="mt-2 bg-slate-900 border-slate-700 text-slate-100 h-12 text-base"
                  placeholder="Smith"
                  required
                  data-testid="intake-last-name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone" className="text-slate-200 text-base">Contact Phone *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="mt-2 bg-slate-900 border-slate-700 text-slate-100 h-12 text-base"
                placeholder="+1 (555) 123-4567"
                required
                data-testid="intake-phone"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-200 text-base">Contact Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="mt-2 bg-slate-900 border-slate-700 text-slate-100 h-12 text-base"
                placeholder="john@company.com"
                required
                data-testid="intake-email"
              />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-700">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-slate-200 text-slate-900 hover:bg-slate-100 font-semibold h-14 text-lg"
              data-testid="request-link-btn"
            >
              {submitting ? 'Submitting...' : 'Request Secure Onboarding Link'}
            </Button>
            <p className="text-slate-400 text-sm text-center mt-4">
              * Required fields. You'll receive a confirmation email after submitting.
            </p>
          </div>
        </form>

        {/* Trust Badges */}
        <div className="mt-10 flex justify-center items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2 bg-slate-800/60 px-4 py-3 rounded-lg border border-slate-700/50">
            <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="18" fill="#0e7490" />
              <text x="20" y="14" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">AICPA</text>
              <text x="20" y="22" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">SOC</text>
              <text x="20" y="30" textAnchor="middle" fill="#a5f3fc" fontSize="5">Certified</text>
            </svg>
            <span className="text-slate-400 text-xs font-medium">AICPA SOC<br/>Certified</span>
          </div>

          <div className="flex items-center gap-2 bg-slate-800/60 px-4 py-3 rounded-lg border border-slate-700/50">
            <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
              <rect x="2" y="2" width="36" height="36" rx="4" fill="#1e293b" stroke="#4ade80" strokeWidth="2" />
              <text x="20" y="13" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">PCI</text>
              <text x="20" y="21" textAnchor="middle" fill="#94a3b8" fontSize="4.5">DSS</text>
              <path d="M12 27 L18 33 L30 21" stroke="#4ade80" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-slate-400 text-xs font-medium">PCI DSS<br/>Service Level 1</span>
          </div>

          <div className="flex items-center gap-2 bg-slate-800/60 px-4 py-3 rounded-lg border border-slate-700/50">
            <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
              <path d="M20 2 L36 10 L36 22 C36 30 28 36 20 38 C12 36 4 30 4 22 L4 10 Z" fill="#334155" stroke="#60a5fa" strokeWidth="1.5" />
              <text x="20" y="18" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">SOC 2</text>
              <text x="20" y="27" textAnchor="middle" fill="#93c5fd" fontSize="5.5">Type II</text>
            </svg>
            <span className="text-slate-400 text-xs font-medium">SOC 2<br/>Type II</span>
          </div>

          <div className="flex items-center gap-2 bg-slate-800/60 px-4 py-3 rounded-lg border border-slate-700/50">
            <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
              <rect x="2" y="2" width="36" height="36" rx="6" fill="#166534" />
              <rect x="13" y="8" width="14" height="10" rx="3" stroke="white" strokeWidth="2" fill="none" />
              <rect x="10" y="16" width="20" height="16" rx="2" fill="white" />
              <circle cx="20" cy="24" r="3" fill="#166534" />
              <rect x="19" y="26" width="2" height="4" rx="1" fill="#166534" />
            </svg>
            <span className="text-slate-400 text-xs font-medium">256-Bit SSL<br/>Encrypted</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-8 border-t border-slate-700/50">
          <div className="flex flex-col items-center gap-4">
            <img src="/paysec-logo.png" alt="PaySec" className="h-8 opacity-80" />
            <p className="text-slate-400 text-sm italic">
              Copyright &copy; {new Date().getFullYear()} &mdash; PaySec LLC. All Rights Reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntakeLandingPage;
